<template>
  <div class="story-detail-wrapper" v-if="localStory">
    <div class="pt-2 pb-2">
      <router-link
        v-if="parentRouteName"
        tag="a"
        class="btn btn-lg"
        :to="{
          name: parentRouteName,
          params: {
            project_id: $route.params.project_id,
            stage_id: $route.params.stage_id,
            user_story_doc_id: $route.params.user_story_doc_id,
            sprint_id: $route.params.sprint_id,
          },
        }"
      >
        <i class="fal fa-arrow-left"></i>
      </router-link>
    </div>

    <div class="row g-3 flex-grow-1 px-3 pb-3" style="min-height: 0; overflow: hidden">
      <div class="col-md-6 col-lg-5 col-xl-4 h-100">
        <div class="user-story-detail d-flex flex-column h-100">
          <div class="top">
            <div class="row user-story-header">
              <div class="col">
                <h1 class="text-truncate">US-{{ localStory.id }}</h1>
              </div>
              <div class="col-auto">
                <span class="status" v-bind:class="statusClass">{{ storyStatus }}</span>
              </div>
            </div>

            <div class="user-story-story">
              <textarea
                style="width: 100%"
                ref="title"
                class="story-detail-title-textarea form-control"
                v-auto-height
                v-if="edit.title"
                @blur="onFieldBlur('title')"
                v-model="localStory.title"
                @keyup="markDirty"
                placeholder="Title"
              ></textarea>
              <div v-else @click="onFieldClick('title')" v-html="getFormattedField('title')"></div>
            </div>

            <div class="row">
              <div class="col-auto">
                <ul class="timers">
                  <li>
                    <span>E</span>
                    {{ localStory.quoted_estimate | toHoursMinutes }}
                  </li>
                  <li>
                    <span>S</span>
                    {{ localStory.time_entries_total | toHoursMinutes }}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="bottom" style="flex-grow: 1; overflow: hidden scroll">
            <div class="user-story-meta">
              <div class="row mb-2">
                <label class="control-label col-4">Tags:</label>
                <div class="col-8">
                  <tag :value="localStory.tags" @input="handleSelectTagCallback" @tag="handleAddNewTag"></tag>
                </div>
              </div>
              <div class="row mb-2">
                <label class="control-label col-4">Assigned:</label>
                <div class="col-8">
                  <user-select :user="localStory.user" :userChanged="userChanged" :inline="true"></user-select>
                </div>
              </div>
            </div>
            <div class="user-story-description">
              <h2 class="strong mt-4">Description</h2>
              <wysiwyg-inline
                v-if="focused"
                ref="description"
                style="width: 100%"
                class="story-detail-textarea form-control"
                v-auto-height
                :content="localStory.description"
                :save="saveDescription"
                :blur="blur"
                :focus="focusWysiwyg"
                placeholder="Description"
              ></wysiwyg-inline>
              <div
                v-else
                @click="wysiwygFocus"
                v-html="localStory.description"
                style="min-height: 40px; min-width: 50px"
              ></div>
            </div>

            <div class="user-story-tech-notes">
              <h2 class="strong mt-4">Technical Notes</h2>
              <textarea
                style="width: 100%"
                ref="tech_notes"
                class="story-detail-textarea form-control"
                v-auto-height
                v-if="edit.tech_notes"
                @blur="onFieldBlur('tech_notes')"
                v-model="localStory.tech_notes"
                @keyup="markDirty"
                placeholder="tech_notes"
              ></textarea>
              <div
                v-else
                @click="onFieldClick('tech_notes')"
                v-html="getFormattedField('tech_notes')"
                style="min-height: 40px; min-width: 50px"
              ></div>
            </div>

            <audio
              v-if="localStory.recording"
              id="audio"
              preload="auto"
              controls="true"
              v-bind:src="localStory.recording"
            ></audio>
          </div>
        </div>
      </div>

      <div class="d-none d-md-block col-md-6 col-lg-7 col-xl-8 h-100">
        <div class="container-project-board container-project-board-detail flex-column">
          <div class="project-board-scroll-x">
            <div class="row row-headings g-0 w-100 mt-1">
              <div class="col">
                <div class="story-heading default">
                  New
                  <span data-bs-toggle="tooltip" data-bs-placement="top" title="" class="count" data-bs-original-title="Unknown">
                    {{ getCountOfStatusAndStoryId('New', localStory.id) }}
                  </span>
                </div>
              </div>
              <div class="col">
                <div class="story-heading warning">
                  In Progress
                  <span data-bs-toggle="tooltip" data-bs-placement="top" title="" class="count" data-bs-original-title="Unknown">
                    {{ getCountOfStatusAndStoryId('In Progress', localStory.id) }}
                  </span>
                </div>
              </div>
              <div class="col">
                <div class="story-heading">
                  Review
                  <span data-bs-toggle="tooltip" data-bs-placement="top" title="" class="count" data-bs-original-title="Unknown">
                    {{ getCountOfStatusAndStoryId('Review', localStory.id) }}
                  </span>
                </div>
              </div>
              <div class="col">
                <div class="story-heading success">
                  Done
                  <span data-bs-toggle="tooltip" data-bs-placement="top" title="" class="count" data-bs-original-title="Unknown">
                    {{ getCountOfStatusAndStoryId('Done', localStory.id) }}
                  </span>
                </div>
              </div>
            </div>

            <div class="row row-user-story g-0 no-after-border flex-grow-1">
              <div
                class="col droppable"
                @drop="dragFinish(localStory.id, 'New', $event)"
                @dragover.prevent
                @dragenter="dragEnter($event)"
                @dragleave="dragLeave($event)"
              >
                <item v-for="item in getByStoryIdAndStatus(localStory.id, 'New')" :key="item.id" :item="item"></item>
                <div class="user-story-actions">
                  <add-item :user-story-id="localStory.id" type="Issue"></add-item>
                  <add-item :user-story-id="localStory.id" type="Task"></add-item>
                </div>
              </div>

              <div
                class="col droppable"
                @drop="dragFinish(localStory.id, 'In Progress', $event)"
                @dragover.prevent
                @dragenter="dragEnter($event)"
                @dragleave="dragLeave($event)"
              >
                <item
                  v-for="item in getByStoryIdAndStatus(localStory.id, 'In Progress')"
                  :key="item.id"
                  :item="item"
                ></item>
              </div>

              <div
                class="col droppable"
                @drop="dragFinish(localStory.id, 'Review', $event)"
                @dragover.prevent
                @dragenter="dragEnter($event)"
                @dragleave="dragLeave($event)"
              >
                <item v-for="item in getByStoryIdAndStatus(localStory.id, 'Review')" :key="item.id" :item="item"></item>
              </div>

              <div
                class="col droppable"
                @drop="dragFinish(localStory.id, 'Completed', $event)"
                @dragover.prevent
                @dragenter="dragEnter($event)"
                @dragleave="dragLeave($event)"
              >
                <item
                  v-for="item in getByStoryIdAndStatus(localStory.id, 'Completed')"
                  :key="item.id"
                  :item="item"
                ></item>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { cloneDeep, filter } from 'lodash'
import Multiselect from 'vue-multiselect'
import { WysiwygInline } from "components/form/input"

import item from './_item'
import addItem from './_add-item'

import userstoryCommon from 'mixins/scrumboard/userstory-common'
import dragAndDrop from 'mixins/scrumboard/drag-and-drop'

export default {
  mixins: [dragAndDrop, userstoryCommon],

  components: {
    item,
    addItem,
    Multiselect,
    WysiwygInline,
  },

  data: function () {
    return {
      localStory: null,
      focused: false,
      focusWysiwyg: false,
    }
  },

  computed: {
    ...mapState('yodizproject/stories', { stories: 'list' }),

    ...mapGetters('yodizproject/tasks', {
      getCountOfStatusAndStoryId: 'getCountOfStatusAndStoryId',
      getByStoryIdAndStatus: 'getByStoryIdAndStatus',
    }),

    ...mapGetters('yodizproject/stories', {
      getById: 'getById',
    }),

    story() {
      return this.getById(this.$route.params.story_id)
    },

    parentRouteName() {
      return this.$route.matched[this.$route.matched.length - 2].name
    },
  },

  watch: {
    //We need to watch the state object to react to any broadcasted updates
    story: function (newVal, oldVal) {
      this.localStory = cloneDeep(this.story)
    },
  },

  mounted: function () {
    this.localStory = cloneDeep(this.story)
  },

  methods: {
    blur() {
      this.focused = false
      this.focusWysiwyg = false
    },

    saveDescription(value) {
      return new Promise(() => {
        this.localStory.description = value
        this.dirty = true
        this.focusWysiwyg = false
        this.focused = false
        this.handleUpdateUserStory()
      })
    },

    wysiwygFocus() {
      this.focusWysiwyg = true
      this.focused = true
    },
  },
}
</script>

<style scoped>
.story-detail-title-textarea {
  height: 201px;
  overflow: hidden;
  padding: 0px;
  border: none;
  width: 100%;
  font-size: 16px;
  font-weight: 500;
}

.story-detail-textarea {
  height: 201px;
  overflow: hidden;
  padding: 0px;
  border: none;
  width: 100%;
  font-size: 16px;
}

ul.timers {
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
  margin-top: 10px;
}

ul.timers li {
  margin-top: 5px;
  display: inline;
  color: #6d6d6d;
}

ul.timers li:not(:first-child) {
  margin-left: 0.375rem;
}

ul.timers span {
  font-weight: 700;
  color: #424242;
}
</style>
